import { useMemo, useState, useContext, useEffect, useRef } from 'react';

import { Image } from 'antd';

import Utils from '../../utils/Utils';

import { LoadingImg } from './components/picturePerview';
import { MroCoupon, PlusCouponTicket, ProductCouponTicket, UnavailableProductCoupon } from './components/productTags';
import TotalPrice from './components/totalPrice';

import { groupValidatePerfect } from '@/apis/so';
import Colon from '@/components/Colon';
import InputSelect from '@/components/InputSelect';
import LabelText from '@/components/LabelText';
import SvgIcon from '@/components/SvgIcon';
import { LayoutContext } from '@/layouts/baseLayout/provider';
import { ProductItemContext } from '@/providers/productItem';
import { ProductListContext } from '@/providers/productList';
import { buriedPoint, goItemBuriedPoint } from '@/utils/buriedPoint';

function GroupItem(props) {
  const { productVO, groupActivityProductVO } = props;

  const { resourceData, isMro, onlySScreen } = useContext(LayoutContext);
  const { track_zone, searchResult } = useContext(ProductListContext);
  const { index, rs_log, newParameterCondition } = useContext(ProductItemContext);
  const [purchaseCount, setPurchaseCount] = useState(null);
  const [loading, setLoading] = useState();
  const [currentTime, setCurrentTime] = useState(null);
  const inputSelectRef = useRef(null);
  const addBtnRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    timer.current = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => {
      timer.current && clearInterval(timer.current);
    };
  }, []);

  const leftTime = useMemo(() => {
    if (!currentTime) {
      return;
    }
    if (!groupActivityProductVO?.groupEndTime) {
      timer.current && clearInterval(timer.current);
      return;
    }
    let during = new Date(groupActivityProductVO?.groupEndTime) - currentTime;
    if (during <= 0) {
      timer.current && clearInterval(timer.current);
      return (
        <div>
          <span>
            剩余
            <Colon />
          </span>
          <span className="text-[14px] font-bold text-[#0093E6]">00</span>天
          <span className="text-[14px] font-bold text-[#0093E6]">00</span>小时
          <span className="text-[14px] font-bold text-[#0093E6]">00</span>分
          <span className="text-[14px] font-bold text-[#0093E6]">00</span>秒
        </div>
      );
    }
    during = during > 0 ? during / 1000 : 0;
    const days = Math.floor(during / (60 * 60 * 24));
    const hours = Math.floor((during % (60 * 60 * 24)) / (60 * 60))
      .toString()
      .padStart(2, '0');
    const minutes = Math.floor((during % (60 * 60)) / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(during % 60)
      .toString()
      .padStart(2, '0');
    return (
      <div>
        <span>
          剩余
          <Colon />
        </span>
        <span className="text-[14px] font-bold text-[#0093E6]">{days}</span>天
        <span className="text-[14px] font-bold text-[#0093E6]">{hours}</span>
        小时
        <span className="text-[14px] font-bold text-[#0093E6]">{minutes}</span>分
        <span className="text-[14px] font-bold text-[#0093E6]">{seconds}</span>秒
      </div>
    );
  }, [currentTime, groupActivityProductVO?.groupEndTime]);

  const buttonText = useMemo(() => {
    if (['B', 'C'].includes(groupActivityProductVO?.recommendLevel)) {
      return '我要订货';
    } else {
      return '购买立推';
    }
  }, [groupActivityProductVO]);

  const totalPrices = useMemo(() => {
    return Math.round(purchaseCount * groupActivityProductVO?.groupPrice * 100) / 100;
  }, [purchaseCount, groupActivityProductVO]);

  function gotoBuy() {
    if (!inputSelectRef?.current?.validate(purchaseCount)) {
      return;
    }
    if (loading) {
      return;
    }
    setLoading(true);
    groupValidatePerfect({
      productId: productVO?.productId,
      purchaseNumber: purchaseCount,
    })
      .then((res) => {
        if (res?.needLogin || res?.msg === 'needLogin') {
          Utils.showWindowLogin();
        } else if (res?.code === 200) {
          let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/member/perfect/group.html?productId=${productVO?.productId}&purchaseNumber=${purchaseCount}`;
          if (track_zone) {
            url += `&trackZone=${Utils.safeEncodeURI(track_zone)}`;
          }
          url = Utils.getBtnSpmQueryUrl(url, addBtnRef.current);
          window.open(url, '_blank');
        }
      })
      .finally(() => {
        setLoading(false);
        buriedPoint({
          op: 'spell_group',
          data: {
            pc: productVO?.productId,
            brand: productVO?.productGradePlateName,
            model: productVO?.productModel,
            code: productVO?.productCode,
            kw: searchResult?.globalSearchKeyword,
            cp: searchResult?.currePage,
            ix: index,
            price: totalPrices,
            rs_log,
            parameter_condition: newParameterCondition,
            track_zone: track_zone,
          },
        });
      });
  }

  // 跳转详情链接
  const detailPage = useMemo(() => {
    if (isMro) {
      return `${resourceData?.projectConfigVO?.lcscItemUrl}/mro/${productVO.productId}.html?fromZone=${Utils.safeEncodeURI(track_zone)}`;
    }
    return `${resourceData?.projectConfigVO?.lcscItemUrl}/${productVO.productId}.html?fromZone=${Utils.safeEncodeURI(track_zone)}`;
  }, [resourceData, isMro, productVO, track_zone]);

  function gotoItem(kind) {
    goItemBuriedPoint({
      kind,
      pc: productVO?.productId,
      brand: productVO?.productGradePlateName,
      model: productVO?.productModel,
      code: productVO?.productCode,
      kw: searchResult?.globalSearchKeyword,
      cp: searchResult?.currePage,
      ix: index,
      track_zone: track_zone,
      rs_log,
    });
  }

  const theRatio = useMemo(() => {
    switch (groupActivityProductVO.additionalPurchaseLimit) {
      case 'split_ratio':
        return groupActivityProductVO?.theRatio;
      case 'min_whole_number':
        return groupActivityProductVO?.minWholeNumber;
      case 'min_packet_number':
        return groupActivityProductVO?.minPacketNumber;
    }
  }, [groupActivityProductVO]);

  // 产品数量单位
  const productUnitDom = useMemo(() => {
    return (
      productVO?.productMinEncapsulationUnit && (
        <p className="mb-[6px]">
          {productVO?.productMinEncapsulationNumber}
          {productVO?.productUnit}/{productVO.productMinEncapsulationUnit}
        </p>
      )
    );
  }, [productVO]);

  return (
    <div className={`flex p-[20px] leading-[18px]`}>
      <div className="flex">
        <div className="w-[121px] h-[105px] select-none relative">
          <a aria-label="点击跳转详情" href={detailPage} target="_blank" onClick={() => gotoItem('picture')}>
            <div className="absolute z-[1] flex h-[20px] w-[40px] items-center rounded-[4px_0_10px_0] bg-[#FF4960] text-[white]">
              <span className="pl-[8px] leading-[15px] text-[white]">立推</span>
            </div>
            <Image
              fallback={'https://static.szlcsc.com/ecp/assets/web/static/images/default_pic.gif'}
              src={'https://static.szlcsc.com/ecp/assets/newWeb/so/lctj.svg'}
              width={105}
              height={105}
              preview={false}
              loading="lazy"
              placeholder={<LoadingImg></LoadingImg>}
              style={{ borderRadius: '4px' }}
              alt={'立推'}
            />
          </a>
        </div>
        <div className={Utils.addBigScreenClassName('w-[178px]', '_lg:w-[215px]', onlySScreen)}>
          <LabelText
            text={productVO?.productModel}
            title={productVO?.productModel}
            isTitle
            textBold
            link={detailPage}
            bp="型号"
            onClick={() => gotoItem('name')}
          ></LabelText>
          <LabelText
            label="品牌"
            text={productVO?.productGradePlateName}
            title={productVO?.productGradePlateName}
          ></LabelText>
          <LabelText
            label="封装"
            text={productVO?.encapsulationModel}
            title={productVO?.encapsulationModel}
          ></LabelText>
        </div>
        <div className={Utils.addBigScreenClassName('w-[190px]', '_lg:w-[233px]', onlySScreen)}>
          <div className="mb-[6px] text-[12px] leading-[16px] text-[#69788a]">{leftTime}</div>
          {groupActivityProductVO?.outerRemark && (
            <LabelText label="备注" text={groupActivityProductVO?.outerRemark}></LabelText>
          )}
          {groupActivityProductVO?.productBatchCode && (
            <LabelText label="批次" text={groupActivityProductVO?.productBatchCode}></LabelText>
          )}
        </div>
      </div>
      <div className={Utils.addBigScreenClassName('w-[157px]', '_lg:w-[180px]', onlySScreen)}>
        <MroCoupon {...props} productType={'lt'} />
        <ProductCouponTicket {...props} productType={'lt'} />
        <PlusCouponTicket {...props} productType={'lt'} />
        <UnavailableProductCoupon {...props} productType={'lt'} />
      </div>
      <div className="flex flex-1 items-center flex-col">
        <div className="flex items-end mb-[1px] -ml-[34px]">
          <SvgIcon app="so" iconName="litui-hot" className="inline-block w-[16px] h-[19px] mr-[2px]"></SvgIcon>
          <span className="font-[400] text-[#FF4960] leading-[16px]">立推售价</span>
        </div>
        <ul className="w-[170px]">
          <li className="flex text-[#333]">
            <label className="w-[70px] text-right">
              {groupActivityProductVO?.minBuyNumber}+<Colon />
            </label>
            <span className="flex-1 text-left">￥{Utils.priceRound(groupActivityProductVO?.groupPrice, 6)}</span>
          </li>
        </ul>
      </div>
      <div className={Utils.addBigScreenClassName('w-[161px]', '_lg:w-[186px]', onlySScreen)}>
        <label className="mb-[6px] inline-block h-[18px] rounded-[3px] bg-[#EDEDED] px-[7px] leading-[18px] text-[#666]">
          支付后{groupActivityProductVO?.productDeliveryDay - 1}-{groupActivityProductVO?.productDeliveryDay + 1}工作日
        </label>
        <LabelText
          label="库存"
          text={Utils.unitConversion(groupActivityProductVO?.surplusRecommendNumber)}
          textBold
        ></LabelText>
        {productUnitDom}
      </div>
      <div className={`${Utils.addBigScreenClassName('w-[120px]', '_lg:w-[140px]', onlySScreen)} text-right`}>
        <InputSelect
          ref={inputSelectRef}
          defaultCount={groupActivityProductVO?.minBuyNumber}
          defaultUnit={productVO?.productUnit}
          productUnit={productVO?.productUnit}
          productMinEncapsulationUnit={productVO?.productMinEncapsulationUnit}
          productMinEncapsulationNumber={productVO?.productMinEncapsulationNumber}
          minBuyNumber={groupActivityProductVO?.minBuyNumber}
          theRatio={theRatio}
          disabled={productVO?.productUnsealVendition === 'no'}
          stockNumber={groupActivityProductVO?.surplusRecommendNumber}
          onChange={(value) => setPurchaseCount(value)}
        ></InputSelect>
        <TotalPrice totalPrices={totalPrices}></TotalPrice>
        <button
          ref={addBtnRef}
          className="mb-[6px] block h-[32px] w-full rounded-[6px] border-[1px] border-solid border-[#1FA0E8] bg-[#EDF9FF] text-[14px] text-[#1FA0E8]
          hover:border-[#0093E6] hover:bg-[#0093E6] hover:text-[white]"
          onClick={gotoBuy}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}

export default GroupItem;
